import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { SectionCard, Th, Td, Empty } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Users, Shield } from "lucide-react";

const ROLES = [
  { value: "admin", label: "Super Admin / Admin" },
  { value: "procurement", label: "Procurement Lead / Buyer" },
  { value: "requisitioner", label: "Branch Manager / Requisitioner" },
  { value: "finance", label: "Finance / Accounts" },
];
const roleLabel = (v) => ROLES.find((r) => r.value === v)?.label || v;

export default function Team() {
  const { user } = useAuth();
  const { meta } = useApp();
  const [data, setData] = useState({ users: [], invites: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", role: "requisitioner", branch: "All" });
  const isAdmin = user?.role === "admin";

  const load = () => api.get("/users").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email) { toast.error("Email required"); return; }
    try { await api.post("/users/invite", form); toast.success("Invite saved — role applies on their first login"); setOpen(false); setForm({ email: "", role: "requisitioner", branch: "All" }); load(); }
    catch { toast.error("Only admin can invite"); }
  };

  const changeRole = async (uid, role, br) => {
    try { await api.put(`/users/${uid}/role`, { role, branch: br }); toast.success("Role updated"); load(); }
    catch { toast.error("Not authorized"); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">Team & Roles</h1>
          <p className="text-brand-slate text-sm mt-1">Manage user access across the 4 procurement roles</p>
        </div>
        {isAdmin && <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="invite-user-button" className="gap-2 bg-brand-teal hover:bg-brand-tealdark active:scale-95 transition-transform"><UserPlus className="w-4 h-4" /> Invite Teammate</Button></DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Invite Teammate</DialogTitle>
              <DialogDescription>The chosen role and branch apply automatically the first time they sign in with Google.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Email (Google account)</Label><Input data-testid="invite-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="teammate@gmail.com" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}><SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Branch</Label>
                <Select value={form.branch} onValueChange={(v) => setForm((f) => ({ ...f, branch: v }))}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="All">All Branches</SelectItem>{meta.branches.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="invite-submit" onClick={invite} className="bg-brand-teal hover:bg-brand-tealdark">Send Invite</Button></DialogFooter>
          </DialogContent>
        </Dialog>}
      </div>

      <SectionCard title={`Active Users (${data.users.length})`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-border"><tr>{["User", "Email", "Role", "Branch", isAdmin ? "Change Role" : ""].map((h) => <Th key={h}>{h}</Th>)}</tr></thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.user_id} data-testid="user-row" className="border-b border-border/60 hover:bg-slate-50">
                  <Td className="font-medium flex items-center gap-2"><img src={u.picture || "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=60&q=80"} className="w-7 h-7 rounded-full object-cover" alt="" /> {u.name}</Td>
                  <Td className="text-slate-600">{u.email}</Td>
                  <Td><span className="inline-flex items-center gap-1 text-xs font-semibold text-navy bg-slate-100 px-2 py-1 rounded-full"><Shield className="w-3 h-3" /> {roleLabel(u.role)}</span></Td>
                  <Td className="text-slate-600">{u.branch || "All"}</Td>
                  {isAdmin && <Td>
                    <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v, u.branch || "All")}>
                      <SelectTrigger data-testid="role-select" className="h-8 w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {data.invites.length > 0 && <SectionCard title={`Pending Invites (${data.invites.length})`}>
        <div className="space-y-2">
          {data.invites.map((i) => (
            <div key={i.email} className="flex items-center justify-between text-sm border-b border-border/60 pb-2">
              <span className="text-navy">{i.email}</span>
              <span className="text-brand-slate">{roleLabel(i.role)} · {i.branch}</span>
            </div>
          ))}
        </div>
      </SectionCard>}
    </div>
  );
}
